import {
  call,
  delay,
  put,
  race,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { Action } from 'typesafe-actions';

import {
  SubscriptionActionTypes,
  SubscriptionSagaConfig,
} from './subscriptionTypes';

import {
  clearAccountSubscription,
  getAccountSubscriptionFailure,
  getAccountSubscriptionSuccess,
  requestGetAccountSubscription,
} from './subscriptionActions';

import { refreshAuthToken } from '../auth/refreshAuthToken';
import { DEFAULT_API_REQUEST_TIMEOUT_MS } from '../constants';

const subscriptionSagaCreator = ({
  apiBaseurl,
  getAuthHeader,
  isNativeApp = false,
}: SubscriptionSagaConfig) => {
  function* handleGetAccountSubscription(
    action: Action<SubscriptionActionTypes.REQUEST_GET_ACCOUNT_SUBSCRIPTION>
  ) {
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(getAccountSubscription),
        timeout: delay(DEFAULT_API_REQUEST_TIMEOUT_MS),
      });

      if (timeout) {
        yield put(
          getAccountSubscriptionFailure(
            new Error('Get account subscription request timed out.')
          )
        );
      } else {
        yield put(getAccountSubscriptionSuccess(response));
      }
    } catch (error: any) {
      yield put(getAccountSubscriptionFailure(error));
    }
  }

  const getAccountSubscription = async () => {
    return fetch(`${apiBaseurl}/api/subscription`, {
      method: 'GET',
      headers: {
        Authorization: await getAuthHeader(),
        'Content-Type': 'application/json',
        'X-Simple-Job': 'true',
        'X-Native-App': `${isNativeApp}`,
      },
    }).then((response) => {
      if (response.ok) {
        return response.json();
      } else {
        throw new Error(
          `GetAccountSubscription failed: ${response.status} ${response.statusText}`
        );
      }
    });
  };

  function* handleLogout() {
    yield put(clearAccountSubscription());
  }

  function* handleAppLoaded() {
    yield put(requestGetAccountSubscription());
  }

  function* handleLoginSuccess() {
    yield put(requestGetAccountSubscription());
  }

  return function* () {
    yield takeLatest(
      SubscriptionActionTypes.REQUEST_GET_ACCOUNT_SUBSCRIPTION,
      handleGetAccountSubscription
    );

    yield takeEvery('auth.logout', handleLogout);
    yield takeEvery('app.loaded', handleAppLoaded);
    yield takeEvery('auth.loginSuccess', handleLoginSuccess);
  };
};

export default subscriptionSagaCreator;
